import { trans } from '@lingoda/i18n';
import { ErrorBoundary } from '@lingoda/monitor';
import { Box, Button, Stack, Typography } from '@lingoda/ui';
import type { PropsWithChildren } from 'react';

/**
 * Catches render-time errors so a single broken page renders a message instead of
 * unmounting the whole app and leaving a blank screen. ErrorBoundary comes from Sentry,
 * so anything it catches is reported.
 *
 * The fallback is deliberately self-contained: ErrorPageLayout from @lingoda/fallbacks
 * reads the auth state through a redux selector and this app has no redux Provider, so
 * using it here would make the fallback itself throw.
 */
const FallbackError = () => {
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                px: 2,
                py: 8,
                minHeight: '60vh',
                textAlign: 'center',
            }}
        >
            <Stack spacing={2} sx={{ alignItems: 'center', maxWidth: 480 }}>
                <Typography variant="h2">
                    {trans('error-page-title', {}, 'teacher-common')}
                </Typography>
                <Typography variant="body1">
                    {trans('error-page-description', {}, 'teacher-common')}
                </Typography>
                <Button
                    variant="outlined"
                    onClick={() => {
                        window.location.reload();
                    }}
                >
                    {trans('error-page-reload', {}, 'teacher-common')}
                </Button>
            </Stack>
        </Box>
    );
};

export const PageErrorBoundary = ({ children }: PropsWithChildren) => {
    return <ErrorBoundary fallback={FallbackError}>{children}</ErrorBoundary>;
};
